import { Box, InputAdornment } from "@mui-wrapper";
import { Checkmark3Icon, Divider, TextField, TextFieldProps } from "@flat-ui";
import FaroExclamationIcon from "@assets/icons/exclamation-mark.svg?react";
import { flagIcons } from "@pages/login/login-server-selector";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { SERVER_IDENTIFIER_LABEL } from "@constants/server-constants";
import { fieldSpacingSx } from "@styles/common-styles";

export interface UlmTextFieldProps {
  /** Input value */
  value: string;

  /** Type of the `input` element */
  type?: React.InputHTMLAttributes<unknown>["type"];

  /** This prop helps users to fill forms faster, it's more like an autofill */
  autoComplete?: string;

  /** Name of the input field, used to get autofill feature */
  name?: string;

  /** Whether the value is validated */
  isValidValue?: boolean;

  /** Whether the input is in error state */
  isError?: boolean;

  /** Text to display when input is in error state */
  errorText?: string;

  /** Text to show user inside the input */
  placeholder: string;

  /** Function that triggers on every input value change */
  onChange?(event: React.ChangeEvent<HTMLInputElement>): void;

  /** Function that triggers on every key event */
  onKeyUp?(event: React.KeyboardEvent<HTMLInputElement>): void;

  /** Optional style props to pass to the component */
  sx?: TextFieldProps["controlStyle"];

  /** True if the text field should be disabled */
  isDisabled?: boolean;

  /** Server that was selected */
  serverIdentifier?: FaroCoreApiTypes.ServerIdentifier;
}

export function UlmTextField({
  value,
  isValidValue,
  isError,
  errorText,
  placeholder,
  onChange,
  onKeyUp,
  type,
  autoComplete,
  name,
  isDisabled = false,
  serverIdentifier,
  sx,
}: UlmTextFieldProps): JSX.Element {
  return (
    <TextField
      text={value}
      fullWidth
      disabled={isDisabled}
      placeholder={placeholder}
      type={type}
      // flat-ui derives the error state from the message rather than from a
      // separate boolean, so an error with no text would render as a valid
      // field. Every call site passes both; the fallback keeps the styling for
      // one that does not.
      error={isError ? (errorText ?? " ") : undefined}
      // In flat-ui `sx` styles the input and `controlStyle` styles the
      // FormControl. The FormControl is what MUI's TextField `sx` reached, so
      // the callers' styles belong there, after the spacing under the field so
      // that a caller can still override it.
      controlStyle={[fieldSpacingSx, ...(Array.isArray(sx) ? sx : [sx])]}
      // flat-ui only offers `onTextChanged(text)`, and the call sites want the
      // event. `InputProps` is spread onto the input last, so these win over
      // flat-ui's own onChange.
      InputProps={{
        name,
        autoComplete,
        onChange,
        onKeyUp,
      }}
      endAdornment={
        <>
          <InputAdornment position="start">
            {isValidValue && <Checkmark3Icon sx={{ color: "primary.main" }} />}
            {isError && <FaroExclamationIcon />}
          </InputAdornment>
          {serverIdentifier && (
            <>
              <Divider orientation="vertical" flexItem />

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  width: "192px",
                  px: "16px",
                  fontSize: "14px",
                }}
              >
                {flagIcons[serverIdentifier]}
                {SERVER_IDENTIFIER_LABEL[serverIdentifier]}
              </Box>
            </>
          )}
        </>
      }
    />
  );
}
